import { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import ChartExportAsPNG from "../../../../Utils/ChartExportAsPng";

const GroupStackedBarChart = ({ data, subcategories, containerClassName, exportRef, selectedCity, groupBy, datasetOne, year }) => {
  const svgRef = useRef();
  const [excludedLegends, setExcludedLegends] = useState([]);
  const containerRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    let resizeTimeout;
    const onResize = () => {
      setIsResizing(true);
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => setIsResizing(false), 200);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        const height = Math.max(400, Math.min(600, width / 2));
        setDimensions({ width, height });
      }
    };

    handleResize();

    const resizeObserver = new ResizeObserver(handleResize);
    if (containerRef.current) resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const colorMap = useRef(new Map());

  const isFirstRender = useRef(true)

  if (colorMap.current.size === 0) {
    const colors = [
      "#003F5C",
      "#2F4B7C",
      "#665191",
      "#a05195",
      "#d45087",
      "#f95d6a",
      "#FF7C43",
      "#FFA600",
    ];
    subcategories.forEach((key, index) => {
      colorMap.current.set(key, colors[index % colors.length]);
    });
  }
  const handleLegendClick = (name) => {
    if (!excludedLegends.includes(name)) {
      setExcludedLegends((prev) => [...prev, name]);
    } else {
      const filtered = excludedLegends.filter((legend) => legend !== name);
      setExcludedLegends(filtered);
    }
  };

  const filteredData = data.map((d) => ({
    ...d,
    values: Object.fromEntries(
      Object.entries(d.values).filter(([key]) => !excludedLegends.includes(key))
    ),
  }));

  useEffect(() => {

    if (!dimensions.width || !dimensions.height) return;
    if (dimensions.width === 0 || dimensions.height === 0) return;

    if (d3.select("body").selectAll(".tooltip").empty()) {
      d3.select("body")
        .append("div")
        .attr("class", "tooltip")
        .style("position", "absolute")
        .style("color", "#fff")
        .style("background", "rgba(0, 0, 0, 0.7)")
        .style("border", "1px solid #ddd")
        .style("border-radius", "4px")
        .style("padding", "8px")
        .style("box-shadow", "0 2px 4px rgba(0,0,0,0.1)")
        .style("font-size", "12px")
        .style("pointer-events", "none")
        .style("opacity", 0)
        .style("z-index", 1000)
        .style("transition", "opacity 0.3s ease, transform 0.2s ease")
        .style("transform", "translateY(5px)");
    }

    const svg = d3.select(svgRef.current);

    if (svg.select(".main-container").empty()) {
      svg.selectAll("*").remove();
    }

    const cleanedData = filteredData.map((d) => {
      const flat = { category: d.category };
      subcategories.forEach((k) => (flat[k] = d.values[k] || 0));
      flat.total = subcategories.reduce((sum, k) => sum + (flat[k] || 0), 0);
      return flat;
    });


    const margin = {
      top: 30,
      right: 50,
      bottom: 50,
      left: 60,
    };

    const width = dimensions.width - margin.left - margin.right;
    const height = dimensions.height - margin.top - margin.bottom;
    const axisFontSize = Math.max(10, Math.min(14, width / 40));

    svg
      .attr("width", dimensions.width)
      .attr("height", dimensions.height)
      .attr("viewBox", `0 0 ${dimensions.width} ${dimensions.height}`)
      .attr("preserveAspectRatio", "xMidYMid meet");

    let mainContainer = svg.select(".main-container");
    if (mainContainer.empty()) {
      mainContainer = svg
        .append("g")
        .attr("class", "main-container")
        .attr("transform", `translate(${margin.left}, ${margin.top})`);
    } else {
      mainContainer.attr(
        "transform",
        `translate(${margin.left}, ${margin.top})`
      );
    }

    const activeSubcategories = subcategories.filter(
      (key) => !excludedLegends.includes(key)
    );

    const stack = d3.stack().keys(activeSubcategories);
    const series = stack(cleanedData);

    const xScale = d3
      .scaleBand()
      .domain(cleanedData.map((d) => d.category))
      .range([0, width])
      .padding(0.2);

    const yMax = d3.max(cleanedData, (d) => d.total) || 100;
    const yScale = d3.scaleLinear().domain([0, yMax]).range([height, 0]).nice();

    // Grid setup
    let gridContainer = mainContainer.select(".grid-container");
    if (gridContainer.empty()) {
      gridContainer = mainContainer.append("g").attr("class", "grid-container");
    }

    // Horizontal grid
    let horizontalGrid = gridContainer.select(".horizontal-grid");
    if (horizontalGrid.empty()) {
      horizontalGrid = gridContainer
        .append("g")
        .attr("class", "horizontal-grid");
    }

    // Bind ticks data
    const yTicks = yScale.ticks();

    const horizontalGridLines = horizontalGrid
      .selectAll(".horizontal-grid-line")
      .data(yTicks);

    // ENTER + UPDATE
    horizontalGridLines
      .join(
        (enter) =>
          enter
            .append("line")
            .attr("class", "horizontal-grid-line")
            .attr("x1", 0)
            .attr("x2", width)
            .attr("stroke", "#e0e0e0"),

        (update) => update,
        (exit) => exit.remove()
      )
      .attr("y1", (d) => yScale(d))
      .attr("y2", (d) => yScale(d))
      .attr("x1", 0)
      .attr("x2", width);

    // Vertical grid
    let verticalGrid = gridContainer.select(".vertical-grid");
    if (verticalGrid.empty()) {
      verticalGrid = gridContainer.append("g").attr("class", "vertical-grid");
    }

    const categories = cleanedData.map((d) => d.category);
 
    const verticalGridLines = verticalGrid
      .selectAll(".vertical-grid-line")
      .data(categories);

    verticalGridLines
      .join(
        (enter) =>
          enter
            .append("line")
            .attr("class", "vertical-grid-line")
            .attr("stroke", "#e0e0e0"),

        (update) => update,
        (exit) => exit.remove()
      )
      .attr("y1", 0)
      .attr("y2", height)
      .attr("x1", (d) => xScale(d) + xScale.bandwidth() / 2)
      .attr("x2", (d) => xScale(d) + xScale.bandwidth() / 2);

    // Right border
    let rightBorder = gridContainer.select(".right-border");
    if (rightBorder.empty()) {
      rightBorder = gridContainer
        .append("line")
        .attr("class", "right-border")
        .attr("stroke", "#e0e0e0")
        .attr("shapeRendering", "crispEdges");
    }

    rightBorder
      .attr("x1", width)
      .attr("x2", width)
      .attr("y1", 0)
      .attr("y2", height);

    // X-axis
    let xAxis = mainContainer.select(".x-axis");
    if (xAxis.empty()) {
      xAxis = mainContainer.append("g").attr("class", "x-axis");
    }

    xAxis.attr("transform", `translate(0, ${height})`);

    // Determine if we need to rotate labels based on available width
    const rotateLabels = (width / cleanedData.length < 120) ;

    xAxis.call(d3.axisBottom(xScale)).call((g) => {
      g.selectAll("path").attr("stroke", "#e0e0e0");
      g.selectAll("line").attr("stroke", "#e0e0e0");
      g.selectAll("text")
       .attr("fill", "gray")
        .attr("transform", rotateLabels ? "rotate(-15)" : "rotate(0)")
        .style("text-anchor", rotateLabels ? "end" : "middle")
        .style("font-size", `${axisFontSize}px`)
        .text((d) => ((d.length > 15  && width<1400))? d.slice(0, 10) + "..." : d) 
       .append("title").text(d => d)

    });

    // Y-axis with responsive ticks
    let yAxis = mainContainer.select(".y-axis");
    if (yAxis.empty()) {
      yAxis = mainContainer.append("g").attr("class", "y-axis");
    }

    yAxis.call(d3.axisLeft(yScale).tickFormat((d) => `${d}%`)).call((g) => {
      g.selectAll("path").attr("stroke", "#e0e0e0");
      g.selectAll("line").attr("stroke", "#e0e0e0");
      g.selectAll("text")
        .attr("fill", "gray")
        .style("font-size", `${axisFontSize}px`);
    });

    // Chart layer
    let chartLayer = mainContainer.select(".chart-layer");
    if (chartLayer.empty()) {
      chartLayer = mainContainer.append("g").attr("class", "chart-layer");
    }

    const tooltip = d3.select("body").select(".tooltip");

    const layers = chartLayer.selectAll("g.layer").data(series, (d) => d.key);

    layers
      .enter()
      .append("g")
      .attr("class", "layer")
      .attr("data-key", (d) => d.key)
      .attr("fill", (d) => colorMap.current.get(d.key))
      .merge(layers)
      .transition()
      .duration(600)
      .attr("fill", (d) => colorMap.current.get(d.key));

    layers.exit().transition().duration(200).style("opacity", 0).remove();

    const handleRectMouseover = (event, d) => {
      tooltip
        .style("opacity", 1)
        .html(
          `
          <p style="margin:0;">
            <strong>${d.data.category}</strong><br/>
            <span style="display:inline-flex; align-items:center; gap:6px;">
              <span style="width:14px; height:14px; background-color:${colorMap.current.get(
            d.key
          )}; border:1px solid rgba(255,255,255,0.7); display:inline-block;"></span>
              <span">${d.key}: ${(d[1] - d[0]).toFixed(2)}%</span>
            </span>
          </p>
        `
        )
        .style("left", `${event.pageX + 5}px`)
        .style("top", `${event.pageY - 28}px`)
        .style("transform", "translateY(0)");
    };

    const handleRectMousemove = (event) => {
      tooltip
        .style("left", `${event.pageX + 10}px`)
        .style("top", `${event.pageY - 40}px`);
    };

    const handleRectMouseout = () => {
      tooltip.style("opacity", 0);
    };
    chartLayer.selectAll("g.layer").each(function (layerData) {
      const layer = d3.select(this);
      const layerKey = layerData.key;

      const rects = layer.selectAll("rect").data(
        layerData.map((d) => ({ ...d, key: layerKey })),
        (d) => `${d.data.category}-${d.key}`
      );

      rects
        .enter()
        .append("rect")
        .attr("x", (d) => xScale(d.data.category))
        .attr("width", xScale.bandwidth())
        .attr("y", d => isFirstRender.current ? yScale(0) : yScale(d[0]))
        .attr("height", 0)
        .attr("fill", colorMap.current.get(layerKey))
        .transition()
        .duration(600)
        .attr("y", (d) => yScale(d[1]))
        .attr("height", (d) => Math.abs(yScale(d[0]) - yScale(d[1])))


      rects
        .transition()
        .duration(isResizing ? 0 : 600)
        .attr("x", (d) => xScale(d.data.category))
        .attr("width", xScale.bandwidth())
        .attr("y", (d) => yScale(d[1]))
        .attr("height", (d) => Math.abs(yScale(d[0]) - yScale(d[1])));




      rects.exit().attr("y", height).attr("height", 0).remove();



      // Tooltip events
      layer
        .selectAll("rect")
        .on("mouseover", handleRectMouseover)
        .on("mousemove", handleRectMousemove)
        .on("mouseout", handleRectMouseout);
    });

    isFirstRender.current = false;
  }, [subcategories, filteredData, excludedLegends]);


  const dataTransform = (data) => {
    const categorySet = new Set();
    const resultMap = {};

    // Collect all unique "values" keys and initialize resultMap
    data.forEach(item => {
      Object.keys(item.values).forEach(valueKey => {
        categorySet.add(valueKey);
        if (!resultMap[valueKey]) {
          resultMap[valueKey] = {};
        }
        resultMap[valueKey][item.category] = item.values[valueKey] || 0;
      });
    });

    // Prepare final array format
    const result = Array.from(categorySet).map(category => ({
      category,
      values: resultMap[category]
    }));

    return result;
  }

  return (
    <div className="flex flex-col">
      <div
        className={containerClassName}
        style={{ height: `${dimensions.height}px` }}
        ref={containerRef}
        id="group-bar-chart-svg"
      >
        <svg ref={svgRef} className="" />
      </div>

      <div className="flex flex-wrap justify-center items-center gap-1  lg:gap-2 xl:gap-3 text-gray-500">
        {subcategories.map((key) => (
          <div
            key={key}
            className="flex justify-center items-center  gap-1 md:gap-2 lg:gap-3 cursor-pointer hover:bg-gray-100  rounded transition-colors"
            onClick={() => handleLegendClick(key)}
          >
            <span
              className="inline-block  h-2 w-5   md:h-3 md:w-8    lg:h-4 lg:w-10 rounded-sm transition-colors"
              style={{ backgroundColor: colorMap.current.get(key) }}
            ></span>

            <button
              className={`${excludedLegends.includes(key)
                ? "line-through opacity-60"
                : "no-underline"
                } text-gray-500 cursor-pointer  text-xs md:text-sm lg:text-base`}
            >
              {key}
            </button>
          </div>
        ))}
       <ChartExportAsPNG
          ref={exportRef}
          targetId="group-bar-chart-svg"
          targetWidth={600}
          fileNamePrefix={datasetOne?.name + ' Group By ' + groupBy?.name + ' - Bar Chart ' + selectedCity?.name}
          chartData={dataTransform(data)}
          chartType="bar"
          imageTitle={datasetOne?.name + ' Group By ' + groupBy?.name + ' - Bar Chart'}
          imageSource={`${selectedCity?.name} ${(selectedCity?.type).charAt(0).toUpperCase() + (selectedCity.type).slice(1).toLowerCase()} - ${year}`}
          tableFont="16px Arial"
          titleFont="bold 20px Arial"
          sourceFont="italic 18px Arial"
          LegendFirstColumn={datasetOne?.name}
          showColorBlock={true}
          colorMap={Array.from(colorMap.current.entries())}
        />
      </div>
    </div>
  );
};

export default GroupStackedBarChart;
